import { ChangeEvent, FC, useCallback, MouseEvent } from 'react';
import { useSelector } from 'react-redux';

import {
  Typography,
  Button,
  Icon,
} from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { BookingObjectTypeSelect } from 'src/components/BookingObjectTypeSelect/BookingObjectTypeSelect';
import { BookingObjectType } from 'src/components/BookingObjectTypeSelect/types';
import {
  BuildingSelect,
  BuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import {
  FloorSelect,
  FloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import { TooltipWrapper } from 'src/components/TooltipWrapper/TooltipWrapper';
import {
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { UploadButton } from 'src/components/UploadButton/UploadButton';
import {
  getAdminBuildingSelectItem,
  getAdminFloorSelectItem,
  getAdminState,
  getAdminTownSelectItem,
} from 'src/store/admin/selectors';
import { uploadPlan } from 'src/store/admin/services';
import { adminActions } from 'src/store/admin/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

import { AdministratorUserPermissions } from '../AdministratorUserPermissions/AdministratorUserPermissions';
import {
  parkingBookingObjectTypeOptions,
  regularBookingObjectTypeOptions,
} from '../consts';

import classes from './AdministratorSettings.module.scss';

interface BaseOptionProps {
  title: string;
  id: number;
  onEdit: (id: number) => void;
  editButtonTitle: string;
}

const BaseOption: FC<BaseOptionProps> = ({
  title,
  id,
  onEdit,
  editButtonTitle,
}) => {
  const handleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onEdit(id);
    },
    [id, onEdit],
  );

  return (
    <div className={classes.optionContainer}>
      <TooltipWrapper title={title}>
        <Typography variant='body2' ellipsis>
          {title}
        </Typography>
      </TooltipWrapper>
      <TooltipWrapper title={editButtonTitle}>
        <Button
          size='small'
          variant='plain'
          startIcon={<Icon iconName={Icons.Edit} />}
          onClick={handleClick}
        />
      </TooltipWrapper>
    </div>
  );
};

interface TownOptionProps {
  option: TownSelectItem;
}

const TownOption: FC<TownOptionProps> = ({ option }) => {
  const dispatch = useAppDispatch();

  const editTown = useCallback(
    (id: number) => {
      dispatch(adminActions.setSelectedTownId(id));
      dispatch(sidesheetActions.open('adminTown'));
    },
    [dispatch],
  );

  return (
    <BaseOption
      title={option.value.townName}
      id={option.value.townId}
      onEdit={editTown}
      editButtonTitle='Редактировать город'
    />
  );
};

const makeTownOption = (option: TownSelectItem): JSX.Element => {
  return <TownOption option={option} />;
};

interface BuildingOptionProps {
  option: BuildingSelectItem;
}

const BuildingOption: FC<BuildingOptionProps> = ({ option }) => {
  const dispatch = useAppDispatch();

  const editBuilding = useCallback(
    (id: number) => {
      dispatch(adminActions.setSelectedBuildingId(id));
      dispatch(sidesheetActions.open('adminBuilding'));
    },
    [dispatch],
  );

  return (
    <BaseOption
      title={option.value.address}
      id={option.value.buildingId}
      onEdit={editBuilding}
      editButtonTitle='Редактировать офис'
    />
  );
};

const makeBuildingOption = (option: BuildingSelectItem): JSX.Element => {
  return <BuildingOption option={option} />;
};

interface FloorOptionProps {
  option: FloorSelectItem;
}

const FloorOption: FC<FloorOptionProps> = ({ option }) => {
  const dispatch = useAppDispatch();

  const editFloor = useCallback(
    (id: number) => {
      dispatch(adminActions.setSelectedFloorId(id));
      dispatch(sidesheetActions.open('adminFloor'));
    },
    [dispatch],
  );

  return (
    <BaseOption
      title={option.value.title}
      id={option.value.floorId}
      onEdit={editFloor}
      editButtonTitle='Редактировать этаж'
    />
  );
};

const makeFloorOption = (option: FloorSelectItem): JSX.Element => {
  return <FloorOption option={option} />;
};

export const AdministratorSettings: FC = () => {
  const dispatch = useAppDispatch();
  const floor = useSelector(getAdminFloorSelectItem);
  const town = useSelector(getAdminTownSelectItem);
  const building = useSelector(getAdminBuildingSelectItem);
  const { backgroundData, selectedPlace, bookingObjectType } =
    useSelector(getAdminState);

  const handleTownChange = useCallback(
    (nextTown: TownSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(adminActions.setTownId(nextTown?.value.townId));
      dispatch(adminActions.removeGhost());
    },
    [dispatch],
  );

  const handleBuildingChange = useCallback(
    (nextBuilding: BuildingSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(adminActions.setBuildingId(nextBuilding?.value.buildingId));
      dispatch(adminActions.removeGhost());
    },
    [dispatch],
  );

  const handleFloorChange = useCallback(
    (nextFloor: FloorSelectItem | undefined) => {
      dispatch(sidesheetActions.close());
      dispatch(adminActions.setFloorId(nextFloor?.value.floorId));
      dispatch(adminActions.removeGhost());
    },
    [dispatch],
  );

  const upload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      await dispatch(uploadPlan(file));
    },
    [dispatch],
  );

  const createTown = useCallback(() => {
    dispatch(adminActions.setSelectedTownId(undefined));
    dispatch(sidesheetActions.open('adminTown'));
  }, [dispatch]);

  const createBuilding = useCallback(() => {
    dispatch(adminActions.setSelectedBuildingId(undefined));
    dispatch(sidesheetActions.open('adminBuilding'));
  }, [dispatch]);

  const createFloor = useCallback(() => {
    dispatch(adminActions.setSelectedFloorId(undefined));
    dispatch(sidesheetActions.open('adminFloor'));
  }, [dispatch]);

  const setBookingObjectType = useCallback(
    (value: BookingObjectType) => {
      dispatch(adminActions.setBookingObjectType(value));
    },
    [dispatch],
  );

  return (
    <div className={classes.actions}>
      <div className={classes.fields}>
        <div className={classes.fieldContainer}>
          <div className={classes.field}>
            <TownSelect
              town={town}
              onChange={handleTownChange}
              dataTestId='administratorTown'
              makeOption={makeTownOption}
            />
          </div>
          <TooltipWrapper title='Добавить город'>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createTown}
              className={classes.button}
            />
          </TooltipWrapper>
        </div>
        <div className={classes.fieldContainer}>
          <div className={classes.field}>
            <BuildingSelect
              building={building}
              onChange={handleBuildingChange}
              townId={town?.value.townId}
              disabled={!town}
              dataTestId='administratorBuilding'
              makeOption={makeBuildingOption}
              key={town?.value.townId}
            />
          </div>
          <TooltipWrapper title='Добавить офис'>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createBuilding}
              className={classes.button}
            />
          </TooltipWrapper>
        </div>
        <div className={classes.fieldContainer}>
          <div className={classes.field}>
            <FloorSelect
              floor={floor}
              onChange={handleFloorChange}
              buildingId={building?.value.buildingId}
              disabled={!building}
              dataTestId='administratorFloor'
              makeOption={makeFloorOption}
              key={building?.value.buildingId}
            />
          </div>
          <TooltipWrapper title='Добавить этаж'>
            <Button
              size='medium'
              startIcon={<Icon iconName={Icons.Add} />}
              onClick={createFloor}
              className={classes.button}
            />
          </TooltipWrapper>
        </div>
      </div>
      <div className={classes.rightActions}>
        {floor && (
          <UploadButton
            onUpload={upload}
            variant='outlined'
            size='medium'
            startIcon={<Icon iconName={Icons.Upload} />}
          />
        )}
        <div className={classes.type}>
          <BookingObjectTypeSelect
            options={
              floor?.value.isParking
                ? parkingBookingObjectTypeOptions
                : regularBookingObjectTypeOptions
            }
            bookingObjectType={bookingObjectType}
            setBookingObjectType={setBookingObjectType}
            disabled={!backgroundData || !!selectedPlace}
          />
        </div>
        <AdministratorUserPermissions />
      </div>
    </div>
  );
};
